import React from 'react'
import { Badge, Box, Divider, Group, Paper, Stack, Text, ThemeIcon } from '@mantine/core'
import { IconBuildingBank, IconCalendarMonth, IconSchool, IconUser } from '@tabler/icons-react'
import { useTheme } from '../../../components/ThemeProvider'

interface EducationItemProps {
  number: number
  item: {
    jsrq: string // 结束日期
    xxxs: string // 学习形式
    xl: string // 学历
    xxlx: string // 学校类型
    zymc: string // 专业名称
    ksrq: string // 开始日期
  }
}

const formatDate = (dateStr: string): string => {
  if (!dateStr || dateStr.length !== 4) return dateStr
  return `20${dateStr.substring(0, 2)}年${dateStr.substring(2, 4)}月`
}

const getEducationColor = (education: string): string => {
  const colorMap: { [key: string]: string } = {
    博士: 'violet',
    硕士: 'blue',
    大学本科: 'cyan',
    大学专科: 'teal',
    高中: 'green',
    中专: 'orange',
    初中: 'yellow',
    小学: 'gray',
  }

  for (const [key, color] of Object.entries(colorMap)) {
    if (education.includes(key)) return color
  }
  return 'blue'
}

const getEducationGradient = (education: string, themeGradient?: string): string => {
  // If theme gradient is available, use it for consistency
  if (themeGradient) return themeGradient

  const gradientMap: { [key: string]: string } = {
    博士: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    硕士: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    大学本科: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    大学专科: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    高中: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    中专: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    初中: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    小学: 'linear-gradient(135deg, #e0c3fc 0%, #9bb5ff 100%)',
  }

  for (const [key, gradient] of Object.entries(gradientMap)) {
    if (education.includes(key)) return gradient
  }
  return 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
}

const EducationItem: React.FC<EducationItemProps> = ({ number, item }) => {
  const theme = useTheme()
  const educationColor = getEducationColor(item.xl)
  const gradientBg = getEducationGradient(item.xl, theme?.primaryColor)

  return (
    <Paper
      p="lg"
      mb="lg"
      radius="lg"
      shadow="sm"
      style={{
        border: '1px solid #f1f3f4',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        transition: 'all 0.3s ease',
        position: 'relative',
        overflow: 'hidden',
      }}
      className="education-card"
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)'
        e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)'
        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)'
      }}
    >
      <Box
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: gradientBg,
        }}
      />

      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Group gap="md">
            <ThemeIcon
              size="lg"
              radius="md"
              variant="gradient"
              gradient={{
                from: theme?.primaryColor || educationColor,
                to: `${theme?.primaryColor || educationColor}.6`,
              }}
            >
              <IconSchool size={20} />
            </ThemeIcon>
            <Text fw={600} size="lg" c="dark">
              学历信息 {number}
            </Text>
          </Group>

          <Badge
            size="lg"
            radius="md"
            variant="gradient"
            gradient={{ from: theme?.primaryColor || educationColor, to: `${theme?.primaryColor || educationColor}.6` }}
            style={{
              fontSize: '13px',
              fontWeight: 600,
              padding: '8px 16px',
              textShadow: '0 1px 2px rgba(0,0,0,0.1)',
            }}
          >
            {item.xl}
          </Badge>
        </Group>

        <Divider variant="dashed" opacity={0.3} />

        <Stack gap="sm">
          <Group gap="md" align="center">
            <ThemeIcon size="sm" variant="light" color={theme?.primaryColor} radius="md">
              <IconUser size={14} />
            </ThemeIcon>
            <Text size="sm" c="dimmed" fw={500} style={{ minWidth: '80px' }}>
              学习形式：
            </Text>
            <Text size="sm" c="dark" fw={500}>
              {item.xxxs}
            </Text>
          </Group>

          <Group gap="md" align="center">
            <ThemeIcon size="sm" variant="light" color={theme?.primaryColor || 'teal'} radius="md">
              <IconBuildingBank size={14} />
            </ThemeIcon>
            <Text size="sm" c="dimmed" fw={500} style={{ minWidth: '80px' }}>
              学校类型：
            </Text>
            <Text size="sm" c="dark" fw={500}>
              {item.xxlx}
            </Text>
          </Group>

          <Group gap="md" align="center">
            <ThemeIcon size="sm" variant="light" color={theme?.primaryColor || 'grape'} radius="md">
              <IconSchool size={14} />
            </ThemeIcon>
            <Text size="sm" c="dimmed" fw={500} style={{ minWidth: '80px' }}>
              专业名称：
            </Text>
            <Text size="sm" c="dark" fw={500}>
              {item.zymc}
            </Text>
          </Group>
        </Stack>

        <Divider variant="dashed" opacity={0.3} />

        <Paper
          p="md"
          radius="md"
          style={{
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            border: '1px solid #e2e8f0',
          }}
        >
          <Group justify="space-between" align="center">
            <Group gap="xs">
              <ThemeIcon size="sm" variant="light" color={theme?.primaryColor || 'orange'} radius="md">
                <IconCalendarMonth size={14} />
              </ThemeIcon>
              <Text size="sm" c="dimmed" fw={500}>
                开始时间
              </Text>
              <Text size="sm" c="dark" fw={600}>
                {formatDate(item.ksrq)}
              </Text>
            </Group>

            <Box
              style={{
                width: '40px',
                height: '2px',
                background: 'linear-gradient(90deg, #cbd5e0 0%, #a0aec0 100%)',
                borderRadius: '1px',
              }}
            />

            <Group gap="xs">
              <ThemeIcon size="sm" variant="light" color={theme?.primaryColor || 'red'} radius="md">
                <IconCalendarMonth size={14} />
              </ThemeIcon>
              <Text size="sm" c="dimmed" fw={500}>
                结束时间
              </Text>
              <Text size="sm" c="dark" fw={600}>
                {formatDate(item.jsrq)}
              </Text>
            </Group>
          </Group>
        </Paper>
      </Stack>
    </Paper>
  )
}

export default EducationItem
